export const NAME_PLACEHOLDER = '#';

export interface CsvValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  orderedIds: number[];
}

export function hasNamePlaceholder(template: string): boolean {
  return template.includes(NAME_PLACEHOLDER);
}

export function buildCollectionName(template: string, index: number): string {
  return template.split(NAME_PLACEHOLDER).join(String(index));
}

export function chunkIds(ids: number[], size: number): number[][] {
  const effectiveSize = Math.max(1, size);
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += effectiveSize) {
    chunks.push(ids.slice(i, i + effectiveSize));
  }
  return chunks;
}

/**
 * Validates the parsed CSV rows and the collection name template, and
 * extracts the ordered, deduped list of numeric ids (CSV row order preserved —
 * this is the order each created collection's items_ref will use).
 */
export function validateCsvRows(rows: any[], idColumn: string, nameTemplate: string): CsvValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const orderedIds: number[] = [];

  if (!rows.length) {
    errors.push('The CSV has no data rows.');
    return { valid: false, errors, warnings, orderedIds };
  }

  if (!(idColumn in rows[0])) {
    errors.push(`The CSV must have a "${idColumn}" column.`);
    return { valid: false, errors, warnings, orderedIds };
  }

  if (!hasNamePlaceholder(nameTemplate)) {
    errors.push(`The collection name template must contain a "${NAME_PLACEHOLDER}" placeholder for the collection number.`);
  }

  const seen = new Set<number>();
  rows.forEach((row, i) => {
    const raw = row[idColumn];
    if (raw === undefined || raw === null || String(raw).trim() === '') return;
    const id = Number(raw);
    if (!Number.isInteger(id)) {
      errors.push(`Row ${i + 1}: "${raw}" in column "${idColumn}" is not a whole number.`);
      return;
    }
    if (seen.has(id)) {
      warnings.push(`Row ${i + 1}: duplicate ${idColumn}=${id} — keeping the first occurrence.`);
      return;
    }
    seen.add(id);
    orderedIds.push(id);
  });

  if (!orderedIds.length && !errors.length) {
    errors.push(`No valid values found in column "${idColumn}".`);
  }

  return { valid: errors.length === 0, errors, warnings, orderedIds };
}
